import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import {  tapResponse } from '@ngrx/operators';
import { SaleDocket } from '../models/sale-docket';
import { Customer } from '../models';
import { SaleDocketService } from '../shared/services/sale-docket.service';
import { ActivatedRoute } from '@angular/router';
import { exhaustMap, pipe, withLatestFrom } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  switchMap,
  tap
} from 'rxjs/operators';
import { CustomerService } from '../shared';
import { ToastrService } from 'ngx-toastr';
import { RetailItemStoreService } from '../shared/retail-item-list/retail-item.store.service';

export interface SaleDocketState {
  docket: SaleDocket | null;
  loading: boolean;
  loadingCustomer: boolean;
  error: string | null;
  customer: Customer;
}

const defaultState: SaleDocketState = {
  docket: null,
  loading: true,
  loadingCustomer: true,
  error: null,
  customer: null
};

@Injectable()
export class SaleDocketStore extends ComponentStore<SaleDocketState> {
  constructor(
    private saleDocketService: SaleDocketService,
    private customerService: CustomerService,
    private activatedRoute: ActivatedRoute,
    private toastrService: ToastrService,
    private retailItemListStore: RetailItemStoreService
  ) {
    super(defaultState);
    this.activatedRoute.params
      .pipe(
        map(({ saleDocketId }) => parseInt(saleDocketId, 10)),
        filter((id) => Boolean(id))
      )
      .subscribe(this.selectDocket);
    this.docket$
      .pipe(
        filter((docket) => Boolean(docket)),
        map(({ customer_id }) => customer_id),
        distinctUntilChanged()
      )
      .subscribe((customerId) => this.selectCustomer(customerId));
  }

  readonly docketLoading$ = this.select((state) => state.loading);
  readonly customerLoading$ = this.select((state) => state.loadingCustomer);
  readonly docket$ = this.select((state) => state.docket);
  readonly currencyRate$ = this.select((state) =>
    state.docket?.currency_rate ? state.docket.currency_rate : 1
  );
  readonly currency$ = this.select((state) => state.docket?.currency_code);
  readonly editable$ = this.select(
    (state) =>
      Boolean(state.docket.finalized_at) === false &&
      state.docket.status !== 'void'
  );
  readonly customer$ = this.select((state) => state.customer);
  readonly itemsLoading$ = this.retailItemListStore.loading$;
  readonly showEposCheckout$ = this.docket$.pipe(
    map((docket) => Boolean(docket.finalized_at) === false)
  );
  readonly canFinalize$ = this.retailItemListStore.items$.pipe(
    mergeMap((items) =>
      this.select(
        (state) =>
          items.length > 0 && Boolean(state.docket.finalized_at) === false
      )
    )
  );
  readonly paymentContainerItemCharges$ = this.retailItemListStore.items$.pipe(
    map((items) =>
      items.map(({ total_price, tax_rate, unit_price, quantity }) => ({
        charge: total_price ?? unit_price * quantity,
        tax_rate
      }))
    )
  );

  private readonly loading$ = this.select(({ loading }) => loading);

  private readonly selectDocket = this.effect<number>(
    pipe(
      tap(() => this.patchState({ loading: true })),
      switchMap((id) => {
        return this.saleDocketService.getSaleDocket(id).pipe(
          tapResponse(
            (docket) =>
              this.patchState({
                docket,
                loading: false
              }),
            (error: Error) =>
              this.patchState({
                error: error.message ?? 'Unable to get sales docket',
                loading: false
              })
          )
        );
      })
    )
  );

  private readonly selectCustomer = this.effect<number>(
    pipe(
      tap(() => this.patchState({ loadingCustomer: true })),
      switchMap((id) => {
        return this.customerService.getCustomer(id).pipe(
          tapResponse(
            (customer) => this.patchState({ customer, loadingCustomer: false }),
            (error: Error) =>
              this.patchState({
                error: error.message ?? 'Unable to get customer',
                loadingCustomer: false
              })
          )
        );
      })
    )
  );

  readonly finalize = this.effect<{ onComplete?: () => void }>(
    pipe(
      withLatestFrom(this.docket$),
      exhaustMap(([handler, docket]) =>
        this.saleDocketService.finalizeSaleDocket(docket.id).pipe(
          tapResponse(
            () => this.toastrService.success('Sales docket finalized'),
            (error: Error) => {
              this.toastrService.error(
                error.message ?? 'Unable to finalize sales docket'
              );
            }
          ),
          tap(() => this.selectDocket(docket.id)),
          tap(() => handler?.onComplete && handler.onComplete())
        )
      )
    )
  );

  readonly checkDocketStatus = this.effect<void>(
    pipe(
      withLatestFrom(this.docket$),
      filter(([, docket]) => Boolean(docket)),
      map(([, docket]) => docket),
      switchMap((docket) =>
        this.saleDocketService.getSaleDocket(docket.id).pipe(
          tapResponse(
            ({ payment_status }) => {
              if (payment_status !== docket.payment_status) {
                this.toastrService.success(
                  `Payment status updated to ${payment_status}`
                );
              }
              this.patchState({ docket: { ...docket, payment_status } });
            },
            (error: Error) => {
              this.toastrService.error(
                error.message ??
                  'Unable to check payment status, please refresh the page'
              );
            }
          )
        )
      )
    )
  );

  readonly updateDocket = this.effect<{
    value: Partial<Pick<SaleDocket, 'purchase_order' | 'account'>>;
    onComplete: () => void;
  }>(
    pipe(
      withLatestFrom(this.docket$),
      filter(([, docket]) => Boolean(docket)),
      map(([{ value, onComplete }, docket]) => ({
        update: { ...docket, ...value },
        onComplete
      })),
      switchMap(({ update, onComplete }) =>
        this.saleDocketService.updateSaleDocket(update.id, update).pipe(
          tapResponse(
            () => this.patchState({ docket: update }),
            (error: Error) => {
              this.toastrService.error(
                error.message ?? 'Unable to update sales invoice'
              );
            }
          ),
          tap(() => onComplete())
        )
      )
    )
  );
}
